import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import {
  MessageCircle, CheckCircle2, ArrowRight, Loader2, Smartphone, Bot, Sparkles,
  ShieldCheck, FileText, TrendingUp,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public trade-fair landing page. Zero-friction path from QR scan → AI on WhatsApp.
 *
 * Auto-detects device:
 *  - Mobile: giant CTA button that opens WhatsApp with `join position-lamp` + AI prompt pre-filled
 *  - Desktop: shows a QR code so the visitor can scan with their phone
 *
 * The 3-step visual guide explains what will happen so first-time users don't panic
 * when Twilio's "You are all set!" reply arrives.
 */
export default function JoinAI() {
  const [cta, setCta] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(
    (navigator.language || '').startsWith('sw') ? 'sw' : 'sw', // default Swahili for TZ
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const isNarrow = window.innerWidth < 768;
    const isUaMobile = /Android|iPhone|iPad|Mobile|WhatsApp/i.test(navigator.userAgent);
    setIsMobile(isNarrow || isUaMobile);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [ctaRes, statusRes] = await Promise.all([
          axios.get(`${API}/api/whatsapp/cta?lang=${lang}`),
          axios.get(`${API}/api/whatsapp/status`),
        ]);
        setCta(ctaRes.data);
        setStatus(statusRes.data);
      } catch (_) {
        setCta(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  const t = lang === 'sw'
    ? {
        badge: 'Wekeza-Smart AI 🇹🇿',
        title: 'Uwekezaji na AI kwenye WhatsApp',
        subtitle: 'Ushauri wa uwekezaji wa saa 24 kutoka kwa AI yetu — hisa za DSE, Hatifungani za Serikali, mifuko ya UTT AMIS.',
        cta: 'Anza AI yako kwenye WhatsApp',
        ctaSub: 'Bofya mara moja — WhatsApp itafunguka na ujumbe ukiwa tayari',
        step1Title: 'Bofya kitufe kikubwa',
        step1Body: 'WhatsApp itafunguka na ujumbe ukiwa tayari ndani.',
        step2Title: 'Bofya Tuma (Send) mara moja',
        step2Body: 'Ujumbe wako utatufikia — AI itakujibu ndani ya sekunde 5.',
        step3Title: 'Anza kuongea na AI yako',
        step3Body: 'Niulize chochote — bei za hisa, hesabu za faida, au ushauri wa uwekezaji.',
        scanTitle: 'Chambua hii kwa simu yako',
        scanBody: 'Fungua kamera ya simu yako kisha ielekeze kwenye QR hii.',
        example: 'Mifano ya maswali',
        examples: [
          '"Nikiwekeza TZS 100,000 kwa CRDB nitapata faida gani?"',
          '"Umoja Fund ni nini na inatoa faida ya asilimia ngapi?"',
          '"Ninaweza kweli kuanza na TZS 10,000 tu?"',
        ],
        fprfHeadline: 'Anza na TZS 10,000',
        fprfSub: 'FPRF · Wekeza Kustaafu — mfuko wa CORE Securities ulioidhinishwa na CMSA',
        fprfPlans: [
          { name: 'Youngsters', age: '18-34', min: 'TZS 5,000/mwezi' },
          { name: 'Middle-Agers', age: '35-54', min: 'TZS 10,000/mwezi' },
          { name: 'Seniors', age: '55+', min: 'TZS 10,000/mwezi' },
        ],
        formsTitle: 'Fomu za CORE Securities',
        formsSub: 'Jibu kwa neno moja WhatsApp upate fomu halisi',
        forms: [
          { label: 'Fungua CDS', keyword: 'CDS' },
          { label: 'Nunua Hisa/Hatifungani', keyword: 'FOMU YA KUNUNUA' },
          { label: 'Jiunge na FPRF', keyword: 'FPRF' },
        ],
        trust: 'Imesajiliwa na CMSA · Ushauri wa kielimu tu',
      }
    : {
        badge: 'Wekeza-Smart AI 🇹🇿',
        title: 'Investment AI on WhatsApp',
        subtitle: '24/7 AI investment coaching — DSE stocks, Treasury Bonds, UTT AMIS unit trusts.',
        cta: 'Activate AI on WhatsApp',
        ctaSub: 'One tap — WhatsApp opens with your message pre-filled',
        step1Title: 'Tap the big button',
        step1Body: 'WhatsApp opens with the message already typed for you.',
        step2Title: 'Tap Send once',
        step2Body: 'Your message reaches us — AI replies within 5 seconds.',
        step3Title: 'Chat with your AI coach',
        step3Body: 'Ask anything — stock prices, return calculations, investment advice.',
        scanTitle: 'Scan this with your phone',
        scanBody: 'Open your phone camera and point it at the QR code.',
        example: 'Example questions',
        examples: [
          '"What return will I get investing TZS 100,000 in CRDB?"',
          '"What is the Umoja Fund and what return does it give?"',
          '"Can I really start with just TZS 10,000?"',
        ],
        fprfHeadline: 'Start with just TZS 10,000',
        fprfSub: 'FPRF · Wekeza Kustaafu — CMSA-approved retirement fund managed by CORE Securities',
        fprfPlans: [
          { name: 'Youngsters', age: '18-34', min: 'TZS 5,000/mo' },
          { name: 'Middle-Agers', age: '35-54', min: 'TZS 10,000/mo' },
          { name: 'Seniors', age: '55+', min: 'TZS 10,000/mo' },
        ],
        formsTitle: 'CORE Securities forms',
        formsSub: 'Reply with one keyword on WhatsApp to get the real form',
        forms: [
          { label: 'Open CDS', keyword: 'CDS' },
          { label: 'Buy Stocks/Bonds', keyword: 'BUY FORM' },
          { label: 'Join FPRF', keyword: 'FPRF' },
        ],
        trust: 'CMSA-registered · Educational advice only',
      };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-emerald-900 to-emerald-950 text-white overflow-x-hidden">
      {/* Textured grain overlay for depth */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%22 height=%22100%22 filter=%22url(%23n)%22/></svg>")',
        }}
      />

      {/* Language toggle */}
      <div className="absolute top-4 right-4 z-10 flex gap-1 bg-black/30 backdrop-blur rounded-full p-1">
        {['sw', 'en'].map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
              lang === l ? 'bg-white text-emerald-900' : 'text-white/70 hover:text-white'
            }`}
            data-testid={`join-lang-${l}`}
          >
            {l === 'sw' ? 'SW' : 'EN'}
          </button>
        ))}
      </div>

      <div className="relative max-w-lg mx-auto px-6 pt-12 pb-16">
        {/* Hero */}
        <div className="text-center mb-10 animate-in fade-in slide-in-from-top-4 duration-700">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-medium mb-6">
            <Sparkles className="w-3 h-3 text-amber-300" />
            {t.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-3 tracking-tight" data-testid="join-title">
            {t.title}
          </h1>
          <p className="text-white/70 text-base leading-relaxed">{t.subtitle}</p>
        </div>

        {/* Sandbox mode banner — only visible when Twilio Sandbox is active */}
        {status?.using_sandbox && (
          <div
            className="mb-6 rounded-xl border border-amber-400/40 bg-amber-500/10 backdrop-blur p-3 flex items-start gap-3"
            data-testid="join-sandbox-banner"
          >
            <ShieldCheck className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-100/90 leading-relaxed">
              <p className="font-semibold text-amber-200 mb-1">
                {lang === 'sw' ? 'Hatua 2 za Kuanza (Demo Mode)' : '2-Step Start (Demo Mode)'}
              </p>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>
                  {lang === 'sw'
                    ? `Bofya kitufe → tuma ujumbe "join ${status.sandbox_join_code || '<code>'}" ulio tayari`
                    : `Tap button → send "join ${status.sandbox_join_code || '<code>'}" (pre-typed)`}
                </li>
                <li>
                  {lang === 'sw'
                    ? 'Baada ya kupokea "You are all set!", bofya kitufe cha pili ⬇️'
                    : 'After "You are all set!", tap the 2nd button ⬇️'}
                </li>
              </ol>
            </div>
          </div>
        )}

        {/* Primary action */}
        {loading || !cta ? (
          <div className="mb-8 h-24 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/60" />
          </div>
        ) : isMobile ? (
          <a
            href={cta.wa_url}
            className="group block relative mb-8"
            data-testid="join-cta-mobile"
          >
            <div className="absolute inset-0 bg-emerald-400 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 group-active:opacity-90 transition" />
            <div className="relative bg-emerald-500 hover:bg-emerald-400 active:scale-[0.98] rounded-2xl p-5 shadow-2xl transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MessageCircle className="w-6 h-6" />
                    <span className="text-lg font-bold">{t.cta}</span>
                  </div>
                  <p className="text-xs text-emerald-50/90 leading-tight">{t.ctaSub}</p>
                </div>
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </a>
        ) : (
          <div className="mb-8 bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-5 flex flex-col items-center gap-3" data-testid="join-cta-desktop">
            <p className="text-sm text-white/80 font-semibold flex items-center gap-2">
              <Smartphone className="w-4 h-4" /> {t.scanTitle}
            </p>
            <div className="bg-white p-3 rounded-xl">
              <QRCodeCanvas value={cta.wa_url} size={200} level="M" includeMargin={false} />
            </div>
            <p className="text-xs text-white/50 text-center">{t.scanBody}</p>
          </div>
        )}

        {/* Sandbox Step 2 — "Say Hi" button that pre-types a greeting so the AI
            kicks in without the user having to think of anything to type. */}
        {status?.using_sandbox && cta && isMobile && (() => {
          const greeting = lang === 'sw'
            ? 'Habari Wekeza-Smart, nataka ushauri wa uwekezaji.'
            : 'Hi Wekeza-Smart, I want investment advice.';
          const number = (cta.number || '').replace(/\D/g, '');
          const step2Url = `https://wa.me/${number}?text=${encodeURIComponent(greeting)}`;
          return (
            <a
              href={step2Url}
              className="group block relative mb-8 -mt-4"
              data-testid="join-cta-step2"
            >
              <div className="relative bg-white/10 hover:bg-white/15 active:scale-[0.98] rounded-2xl p-4 border border-white/15 transition-all">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 text-emerald-950 text-[11px] font-black">2</span>
                      <span className="text-sm font-bold">
                        {lang === 'sw' ? 'Baada ya kujiunga — bofya hapa' : 'After joining — tap here'}
                      </span>
                    </div>
                    <p className="text-[11px] text-white/60 leading-tight pl-7">
                      {lang === 'sw'
                        ? 'Hutuma "Habari" moja kwa moja → AI itakujibu papo hapo.'
                        : 'Sends "Hi" automatically → AI replies instantly.'}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/70 group-hover:translate-x-1 transition-transform shrink-0" />
                </div>
              </div>
            </a>
          );
        })()}

        {/* 3-step guide */}
        <div className="space-y-3 mb-10">
          {[
            { icon: Smartphone, title: t.step1Title, body: t.step1Body },
            { icon: CheckCircle2, title: t.step2Title, body: t.step2Body },
            { icon: Bot, title: t.step3Title, body: t.step3Body },
          ].map((s, i) => (
            <div
              key={i}
              className="flex items-start gap-4 bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4"
              data-testid={`join-step-${i + 1}`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-emerald-950 font-bold shrink-0 shadow-lg">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <s.icon className="w-4 h-4 text-emerald-300" />
                  <h3 className="text-sm font-semibold">{s.title}</h3>
                </div>
                <p className="text-xs text-white/60 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* FPRF product highlight — "Anza na TZS 10,000" */}
        <div className="mb-6 relative overflow-hidden rounded-2xl border border-amber-300/30 bg-gradient-to-br from-amber-500/10 via-emerald-800/40 to-emerald-950/60 backdrop-blur p-5" data-testid="join-fprf-card">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400/90 text-emerald-950 flex items-center justify-center shrink-0 shadow-lg">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold leading-tight" data-testid="join-fprf-headline">{t.fprfHeadline}</h3>
              <p className="text-xs text-white/60 leading-relaxed mt-0.5">{t.fprfSub}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {t.fprfPlans.map((p, i) => (
              <div
                key={i}
                className="bg-black/25 border border-white/10 rounded-lg p-2 text-center"
                data-testid={`join-fprf-plan-${p.name.toLowerCase()}`}
              >
                <p className="text-[11px] font-semibold text-amber-200">{p.name}</p>
                <p className="text-[10px] text-white/50 mt-0.5">{p.age}</p>
                <p className="text-[10px] text-white/80 mt-1 font-medium">{p.min}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top-3 CORE Securities forms */}
        <div className="mb-8" data-testid="join-forms-row">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-3.5 h-3.5 text-emerald-300" />
            <p className="text-xs font-semibold uppercase tracking-wider text-white/60">{t.formsTitle}</p>
          </div>
          <p className="text-[11px] text-white/40 mb-2">{t.formsSub}</p>
          <div className="grid grid-cols-3 gap-2">
            {t.forms.map((f, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-center hover:bg-white/10 transition"
                data-testid={`join-form-${f.keyword.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="text-[11px] font-semibold text-white/90 leading-tight">{f.label}</p>
                <p className="text-[10px] text-emerald-300 mt-1 font-mono">"{f.keyword}"</p>
              </div>
            ))}
          </div>
        </div>

        {/* Example prompts */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-2">{t.example}</p>
          <div className="space-y-2">
            {t.examples.map((ex, i) => (
              <div key={i} className="text-sm bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white/80">
                {ex}
              </div>
            ))}
          </div>
        </div>

        {/* Trust badge */}
        <p className="text-center text-[11px] text-white/40 uppercase tracking-widest">{t.trust}</p>
      </div>
    </div>
  );
}
